<template>
	<AppDialog disableMaxWidth :width="dialogWidth" ref="dialog_container" dialogName="EditEmailTemplateDialog">
		<Col class="edit-template-dialog pa-1">
			<div class="container">
				<div id="bar">
					<Row class="header flex-justify-space-between width-100 pa-1">
						<Row>
							<AppIcon @click.native="onBackButtonClicked" class="cursor-pointer align-self-center" name="left-black-arrow" />
							<h2 class="font-size-lg font-weight-500" ref="template-title">
								<AppTextField
									@blur="onBlur"
									@keyup.enter="onBlur"
									:readonly="isTemplateFieldDisabled"
									class="template-title"
									:class="[isTemplateFieldDisabled ? '' : 'text-underline']"
									flat
									dense
									hide-details
									v-model="emailTemplateTitle"
									solo
								/>
							</h2>
						</Row>
						<Row class="px-2">
							<AppVMenu
								left
								offset-x
								rounded
								open-on-hover
								nudge-top="0"
								nudge-left="0"
								min-width="360"
								max-width="500"
								:close-on-content-click="false"
							>
								<template v-slot:activator="{ on, attrs }">
									<div v-on="on" v-bind="attrs">
										<div class="header-btn font-size-md font-weight-400 secondary-color-text text-underline cursor-pointer">
											How to map data?
										</div>
									</div>
								</template>
								<Col class="menu-content-container py-6 px-4">
									<h2 class="bold mb-3">How it works</h2>
									<h3 class="reg">
										1. Define dynamic content in your template using {{ `\{\{dynamic_field_name\}\}` }} <br />
										2. Map data from your feature when you send an email
									</h3>
									<h3 class="reg mt-3 mb-4">
										For example, you can greet your recipient by writing: Hello {{ '\{\{first_name\}\}' }} You’ll be able to map
										{{ '\{\{first_name\}\}' }} when you set up an email step.<br />
									</h3>
									<h3>
										<h2 class="bold mb-3">Tips</h2>
										<ul class="tips-ul">
											<li>You can map the same content in multiple places when you use the exact dynamic content label</li>
											<li>You can use dynamic content in images and links too, it works in the same way</li>
										</ul>
									</h3>
									<div class="redirect-link link mt-4" @click="openLearnMoreLink">Learn more</div>
								</Col>
							</AppVMenu>

							<v-btn disabled style="background: unset !important" class="ml-1" v-if="didSaveAtLeastOnce && isSaveDisabled"> Saved </v-btn>
							<AnemicButton small disabled v-else-if="isSaveDisabled" class="header-btn secondary-color-text" @click="onSaveDesign"
								>Save</AnemicButton
							>
							<PrimaryButton v-else class="header-btn" @click="onSaveDesign">Save</PrimaryButton>
							<span class="actions-menu">
								<ActionsButton
									:template="loadedEmailTemplate"
									@rename-template="renameTemplate"
									@duplicate-template="onDuplicateTemplate"
									@delete-template="onDeleteTemplateClicked"
									@set-default-template="onSetDefaultTemplate"
								/>
							</span>
						</Row>
					</Row>
				</div>
				<div class="mt-5 pt-4 main-template-content">
					<div id="editor" ref="editEmailEditor"></div>
				</div>
			</div>
		</Col>
	</AppDialog>
</template>

<script>
import ActionsButton from './EmailTemplateReusables/ActionsButton.vue';
import AppDialog from '@/components/dialogs/AppDialog/AppDialog';
import AppDialogManagerService from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';
import { APP_CONFIRMATION_DIALOG_RESPONSES } from '@/components/dialogs/AppDialogs/app-confirmation-dialog.const';
import { setDocsOpen } from '@/store/modules/onboarding/actions';
import {
	fetchEmailTemplates,
	updateEmailTemplate,
	deleteTemplate,
	duplicateTemplate,
	fetchWorkflowsRelatedToTemplateId,
	setDefaultEmailTemplate,
	fetchAllEmailTemplates,
} from '@/store/modules/transactionalEmailTemplate/actions';
import { usePublicEmailTemplatesArr, useEmailTemplatesArr } from '@/store/modules/transactionalEmailTemplate/getters';
import { mapActionsShorthand, mapGettersShorthand } from '@/store/utils/mappers';
import { isNullOrUndefined } from '@/utils/helpers';
import { TRANSACTIONAL_EMAIL_DOCS_URL } from '@/utils/transactional-emails.const';

export default {
	name: 'EditEmailTemplateDialog',
	components: { AppDialog, ActionsButton },
	props: { dialogPromiseHandlers: { type: Object }, templateId: { type: Number }, templateTitle: { type: String } },
	async mounted() {
		await Promise.all([this.fetchAllEmailTemplates(), this.setupUsedIn()]);
		if (this.emailEditor) {
			unlayer.init({
				id: 'editor',
				displayMode: 'email',
				projectId: 242660,
			});
			this.setUnlayerIframeStyleIfNeeded();
			unlayer.addEventListener('editor:ready', this.onEditorReady);
			unlayer.addEventListener('design:loaded', this.onDesignLoaded);
			unlayer.addEventListener('design:updated', this.onDesignUpdated);
		}
	},
	computed: {
		...mapGettersShorthand({ useEmailTemplatesArr, usePublicEmailTemplatesArr }),
		emailEditor() {
			return this.$refs.editEmailEditor;
		},
		isSaveDisabled() {
			return this.currentDesign === this.stringifiedInitialDesign;
		},
		allTemplates() {
			return [...this.privateTemplates.toReversed(), ...this.publicTemplates];
		},
		privateTemplates() {
			return this.useEmailTemplatesArr;
		},
		publicTemplates() {
			return this.usePublicEmailTemplatesArr;
		},
		loadedEmailTemplate() {
			return this.allTemplates.find((template) => template.id === this.templateId);
		},
		isTemplateFieldDisabled() {
			return !this.isInRenameMode;
		},
		numOfFeaturesUsed() {
			return this.usedInWorkflowsInfo?.count;
		},
		hasNumberOfFeaturesUsed() {
			return !isNullOrUndefined(this.numOfFeaturesUsed);
		},
	},
	methods: {
		...mapActionsShorthand({
			fetchAllEmailTemplates,
			setDefaultEmailTemplate,
			fetchEmailTemplates,
			updateEmailTemplate,
			deleteTemplate,
			duplicateTemplate,
			fetchWorkflowsRelatedToTemplateId,
			setDocsOpen,
		}),
		openLearnMoreLink() {
			this.setDocsOpen(TRANSACTIONAL_EMAIL_DOCS_URL);
		},
		setUnlayerIframeStyleIfNeeded() {
			// Unlayer iframe has min-width: 1024px in it's style attribute.
			const mql1150 = window.matchMedia('(max-width: 1150px)');
			if (!mql1150.matches) {
				return;
			}
			const unlayerEditorIframe = document.querySelector('#editor iframe');
			unlayerEditorIframe.style.setProperty('min-width', '500px');
		},
		async onBackButtonClicked() {
			if (this.isSaveDisabled) {
				this.resolveWithValue({ openChangeEmailTemplateDialog: true });
				return;
			}
			const dialogProps = {
				title: 'You have unsaved changes',
				description: 'Save your information before leaving?',
				yesText: 'Save changes and continue ',
				noText: 'Continue without saving',
			};
			const shouldSaveChanges = await this.checkIfShouldSaveDesign(dialogProps);
			if (shouldSaveChanges) {
				await this.saveDesign();
			}
			const shouldOpenChangeDialog = typeof shouldSaveChanges === 'boolean'; // If it's not boolean, then the dialog was just closed.
			if (!shouldOpenChangeDialog) {
				return;
			}
			this.resolveWithValue({ openChangeEmailTemplateDialog: true });
		},
		async onBlur() {
			if (!this.isInRenameMode) {
				return;
			}
			// Makes sure to set the created template back to uneditable
			this.isInRenameMode = false;
			try {
				await this.updateEmailTemplate({ id: this.templateId, payload: { title: this.emailTemplateTitle } });
			} catch (e) {
				console.error('Failed to update email template title', e);
				this.$toast.error('Failed to rename email template');
			}
		},
		onEditorReady() {
			unlayer.loadDesign(this.loadedEmailTemplate.design);
		},
		onDesignLoaded() {
			unlayer.saveDesign(({ body: designBody }) => {
				const stringifiedDesign = JSON.stringify(designBody);
				this.stringifiedInitialDesign = stringifiedDesign;
				this.currentDesign = stringifiedDesign;
			});
		},
		onDesignUpdated() {
			unlayer.saveDesign(({ body: designBody }) => {
				this.currentDesign = JSON.stringify(designBody);
			});
		},
		close() {
			AppDialogManagerService.closeCurrentDialog();
		},
		resolveWithValue(...args) {
			this.dialogPromiseHandlers.resolve(...args);
			this.close();
		},
		async checkIfShouldSaveDesign(dialogProps) {
			return await AppDialogManagerService.openSemiConfirmationDialog(dialogProps);
		},
		async onSaveDesign() {
			if (this.numOfFeaturesUsed === 0) {
				await this.saveDesign();
				return;
			}
			const dialogProps = {
				title: `This template is used in ${this.numOfFeaturesUsed} features\nsaving will change it in all of them.`,
				yesText: 'Save',
				noText: 'Cancel',
				preWrapTitle: true,
			};
			const shouldSaveDesign = await this.checkIfShouldSaveDesign(dialogProps);
			if (shouldSaveDesign) {
				await this.saveDesign();
			}
		},
		async setupUsedIn() {
			try {
				this.usedInWorkflowsInfo = await this.fetchWorkflowsRelatedToTemplateId({ templateId: this.templateId });
			} catch (e) {
				console.error('Failed to fetch workflows related to this template');
				this.usedInWorkflowsInfo = null;
			}
		},
		promisifySaveDesign() {
			return new Promise((resolve) => unlayer.saveDesign(resolve));
		},
		promisifyExportHtml() {
			return new Promise((resolve) => unlayer.exportHtml(resolve));
		},
		async saveDesign() {
			const [design, { html }] = await Promise.all([this.promisifySaveDesign(), this.promisifyExportHtml()]);
			try {
				await this.updateEmailTemplate({ id: this.templateId, payload: { design, html } });
			} catch (e) {
				console.error('Failed to update email template ', e);
				this.$toast.error('Failed to update email template');
				return;
			}
			const stringifiedDesign = JSON.stringify(design.body);
			this.stringifiedInitialDesign = stringifiedDesign;
			this.currentDesign = stringifiedDesign;
			this.didSaveAtLeastOnce = true;
		},
		showCursorOnTemplate() {
			const selectedTemplate = this.$refs['template-title'];
			const inputEl = selectedTemplate.querySelectorAll('input')[0];
			if (!inputEl) {
				console.error('No input element found under element:', selectedTemplate?.$el);
				return;
			}
			inputEl.select();
		},
		renameTemplate() {
			this.isInRenameMode = true;
			this.showCursorOnTemplate();
		},
		async onDuplicateTemplate({ id: templateId, isPublic }) {
			await this.duplicateTemplate({ templateId, isPublic });
		},
		async onSetDefaultTemplate(templateId) {
			try {
				await this.setDefaultEmailTemplate({ templateId });
			} catch (e) {
				console.error('Failed to set default template', e);
				this.$toast.error('Failed to set default template, only account admins can set templates as default');
			}
		},
		async onDeleteTemplateClicked(templateId) {
			const res = await AppDialogManagerService.openConfirmationDialog({
				title: 'Delete template',
				description: 'Are you sure you want to delete this template?',
				yesText: 'Delete',
				noText: 'Cancel',
			});
			if (res == APP_CONFIRMATION_DIALOG_RESPONSES.YES) {
				// May not be the best UX to close the dialog after deletion, consult with Suda
				await this.deleteTemplate(templateId);
				this.close();
			}
		},
	},
	data() {
		const mql1550 = window.matchMedia('(max-width: 1550px)');
		const dialogWidth = mql1550.matches ? '98vw' : '75vw';
		return {
			dialogWidth,
			options: { displayMode: 'email' },
			emailTemplateTitle: this.templateTitle,
			isInRenameMode: false,
			stringifiedInitialDesign: null,
			currentDesign: null,
			didSaveAtLeastOnce: false,
			usedInWorkflowsInfo: null,
		};
	},
};
</script>

<style scoped lang="scss">
.main-template-content {
	& > div {
		height: 50rem !important;
		@media (max-width: 1550px) {
			height: 40rem !important;
		}
		@media (max-width: 1250px) {
			height: 30rem !important;
		}
	}
}
.edit-template-dialog {
	border-radius: 15px !important;
	box-shadow: 0px 3px 6px rgba(0, 0, 0, 0.16) !important;
	padding: 20px 30px 14px 30px;

	.template-title {
		width: 300%;
	}
	::v-deep {
		.v-icon {
			font-size: 16px !important;
		}
	}
}

.header {
	border-bottom: 2px solid #e4e8f3;
	padding-bottom: 12px !important;
}

.header-btn {
	margin-left: 15px;
	padding-top: 8px;
	padding-bottom: 4px;
	padding-left: 10px;
	min-width: 80px;
	min-height: 35px;
	text-align: center;
}

.disabled-color-text {
	// color seems to have no effect with v-btn disabled
	color: var(--Colors-Neutral-Text-colorTextDisabled, #c1c5cf) !important;
}
.secondary-color-text {
	color: var(--Colors-Neutral-Text-colorTextSecondary, #646e87);
}
.background-color-unset {
	background-color: unset !important;
}

.actions-menu {
	cursor: pointer;
	margin-right: 12px;
	margin-left: 6px;
	padding-right: 10px;
	padding-left: 6px;
	text-align: center;
}

// tooltip (AppVMenu) styling
.menu-content-container {
	border-radius: 6px;
	background: #ffffff;
	border: 1px solid #e5e8ef;
	box-shadow: 0px 5px 26px #d6d6d6;
}
.second-container {
	border-radius: 8px;
	background: #f9fafc;
	border: 1px solid #e5e8ef;
}
.tips-ul {
	list-style: unset;
}
.redirect-link {
	text-decoration: underline;
	letter-spacing: 0px;
	font-size: 14px;
	font-weight: normal;
}
.container {
	@media (min-width: 1280px) {
		max-width: 100% !important;
	}
}
</style>
